import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Shell from "../components/Shell.jsx";
import SnapReceiptButton from "../components/ReceiptCapture.jsx";
import CardScanButton, { cardFieldsToPrefill, isLowConfidence } from "../components/CardScan.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { enrollBiometric, platformAuthAvailable, hasDevicePasskey } from "../lib/webauthn.js";
import { api } from "../lib/api.js";
import { cachedGet } from "../lib/outbox.js";
import { TASK_ICON, taskDueInfo } from "./Crm.jsx";

function FaceIdBanner() {
  const { user, refresh } = useAuth();
  const [supported, setSupported] = useState(false);
  const [state, setState] = useState("idle"); // idle | busy | done | error

  useEffect(() => {
    platformAuthAvailable().then(setSupported);
  }, []);

  // Per-DEVICE check: an enrollment on the desktop must not hide the banner on
  // the phone — each device needs its own passkey. And the device's own marker
  // is only half the evidence: the server must still hold a credential for
  // THIS hostname. After a production-to-staging mirror the staging
  // credential is gone while the browser's marker remains, and the banner
  // vanished with no way back to Touch ID — so both must agree to hide it.
  if (!supported || !user || (hasDevicePasskey(user.id) && user.has_biometric) || state === "done") return null;
  // The demo enrolls nothing on anyone's device — say what the live app offers.
  if (user.demo)
    return (
      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-semibold text-amber-900">Face ID sign-in (live app)</p>
        <p className="text-xs text-amber-900/80">
          Demo mode — enrollment is turned off here. In the live app, each phone or laptop can enroll Face ID / Touch
          ID once and skip the PIN from then on.
        </p>
      </section>
    );

  async function enable() {
    setState("busy");
    try {
      await enrollBiometric(user.id);
      await refresh();
      setState("done");
    } catch {
      setState("error");
    }
  }

  return (
    <section className="rounded-2xl border border-navy-600/20 bg-navy-700/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-navy-700">Sign in faster with Face ID</p>
          <p className="text-xs text-slate-500">Skip the PIN on this device.</p>
          {state === "error" && (
            <p className="mt-1 text-xs text-red-600">Didn't work — try again, or keep using your PIN.</p>
          )}
        </div>
        <button
          onClick={enable}
          disabled={state === "busy"}
          className="shrink-0 rounded-xl bg-navy-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {state === "busy" ? "Waiting…" : "Enable"}
        </button>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [activeTrips, setActiveTrips] = useState([]);
  const [stale, setStale] = useState(false);

  const loadTasks = () =>
    cachedGet("/api/crm/tasks")
      .then(({ data, stale: s }) => {
        setTasks((data.tasks || []).filter((t) => !t.done));
        if (s) setStale(true);
      })
      .catch(() => setTasks([]));

  useEffect(() => {
    loadTasks();
    cachedGet("/api/mileage/trips?active=1")
      .then(({ data }) => setActiveTrips(data.active || []))
      .catch(() => {});
  }, []);

  async function completeTask(t) {
    setTasks((ts) => ts.filter((x) => x.id !== t.id)); // optimistic
    await api(`/api/crm/tasks/${t.id}`, { method: "PUT", body: { done: true } }).catch(loadTasks);
  }

  return (
    <Shell>
      <FaceIdBanner />
      {stale && (
        <p className="rounded-xl bg-slate-100 px-4 py-2 text-center text-xs text-slate-500">
          Offline — showing last synced data
        </p>
      )}

      {/* Quick actions: cameras first (receipts + business cards), manual entries beside them.
          No financials on the home screen — demoing the app must never expose numbers. */}
      <div className="grid grid-cols-2 gap-3">
        <SnapReceiptButton
          className="rounded-2xl bg-navy-700 py-4 text-center text-base font-semibold text-white shadow-sm disabled:opacity-60"
          onCaptured={({ id }) => navigate(`/receipts/${id}/review`)}
        />
        <Link
          to="/add"
          className="flex items-center justify-center rounded-2xl border-2 border-navy-700/30 bg-white py-4 text-center text-base font-semibold text-navy-700 shadow-sm"
        >
          ＋ Add expense
        </Link>
        <CardScanButton
          className="rounded-2xl bg-navy-600 py-4 text-center text-base font-semibold text-white shadow-sm disabled:opacity-60"
          onExtracted={(fields) =>
            navigate("/crm/new", {
              state: { prefill: cardFieldsToPrefill(fields), lowConfidence: isLowConfidence(fields) },
            })
          }
        />
        <Link
          to="/crm/new"
          className="flex items-center justify-center rounded-2xl border-2 border-navy-700/30 bg-white py-4 text-center text-base font-semibold text-navy-700 shadow-sm"
        >
          👤 Add contact
        </Link>
        <Link
          to="/mycard"
          className="flex items-center justify-center rounded-2xl bg-slate-800 py-4 text-center text-base font-semibold text-white shadow-sm"
        >
          🔳 My card QR
        </Link>
        <Link
          to="/dashboard"
          className="flex items-center justify-center rounded-2xl border-2 border-navy-700/30 bg-white py-4 text-center text-base font-semibold text-navy-700 shadow-sm"
        >
          📊 Dashboard
        </Link>
        <Link
          to="/mileage"
          className={`col-span-2 flex items-center justify-center rounded-2xl py-4 text-center text-base font-semibold shadow-sm ${
            activeTrips.length
              ? "border border-amber-300 bg-amber-50 text-amber-900"
              : "border-2 border-navy-700/30 bg-white text-navy-700"
          }`}
        >
          {activeTrips.length ? "🚗 Trip in progress — tap to end it" : "🚗 Start a trip (snap odometer)"}
        </Link>
      </div>

      <section className="rounded-2xl bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 pt-4">
          <p className="text-sm font-semibold text-slate-700">Open follow-ups</p>
          <Link to="/crm?tab=tasks" className="text-xs font-semibold text-navy-700">
            See all →
          </Link>
        </div>
        {tasks === null && <p className="px-4 py-6 text-center text-sm text-slate-400">Loading…</p>}
        {tasks?.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-slate-400">Nothing due — you're all caught up 🎉</p>
        )}
        <div className="divide-y divide-slate-100">
          {(tasks || []).slice(0, 5).map((t) => {
            const d = taskDueInfo(t);
            return (
              <div key={t.id} className="flex items-center gap-3 px-4 py-3">
                <button onClick={() => completeTask(t)} className="shrink-0 text-xl">
                  ⬜️
                </button>
                <div
                  className="min-w-0 flex-1"
                  onClick={() => (t.contact_id ? navigate(`/crm/${t.contact_id}`) : navigate("/crm?tab=tasks"))}
                >
                  <p className="truncate text-sm font-medium text-slate-800">
                    {t.priority ? "🔴 " : ""}
                    {TASK_ICON[t.task_type] || "🔔"} {t.title}
                  </p>
                  <p className={`truncate text-xs ${d.overdue ? "font-semibold text-red-600" : "text-slate-400"}`}>
                    {t.contact_name ? `${t.contact_name} · ` : ""}
                    {d.label}
                    {d.overdue ? " — overdue" : ""}
                  </p>
                </div>
                <span className="shrink-0 text-slate-300">›</span>
              </div>
            );
          })}
        </div>
        <div className="h-2" />
      </section>
    </Shell>
  );
}
